import { AuditLog } from '@frontend/audit/types';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { ToastUtil } from '@frontend/toast-utils';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

export interface AuditDataModalProps {
    audit: AuditLog;
    onClose: () => void;
}

export const AuditDataModal = (props: AuditDataModalProps) => {
    return (
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-center justify-center gap-6 rounded-2xl p-6 outline-1 outline-offset-[-1px]'>
            <div className='inline-flex flex-col items-start justify-start gap-2 self-stretch'>
                <div className='inline-flex items-start justify-between self-stretch'>
                    <div className='flex flex-col'>
                        <div className='text-foreground-default text-lg font-semibold'>{props.audit.entity_id}</div>
                        <div className='text-foreground-default text-lg font-semibold'>{props.audit.entity_type}</div>
                        <div className='text-foreground-default text-lg font-semibold'>{props.audit.type}</div>
                    </div>
                    <ICONS.BUTTON.CLOSE
                        className='hover:cursor-pointer'
                        onClick={props.onClose}
                    />
                </div>
                <div className='text-foreground-default flex w-full justify-between rounded py-2 pe-2 text-sm'>
                    <div className='text-foreground-secondary text-md'>{CommonMessage.DETAIL.DETAILS}</div>
                    <ICONS.BUTTON.COPY
                        className='hover:text-brand-primary size-4 hover:cursor-pointer'
                        onClick={async () => {
                            await navigator.clipboard.writeText(JSON.stringify(props.audit.data, null, 2));
                            ToastUtil.success(
                                <FormattedMessage
                                    id='AuditDataModal.COPY_SUCCESS'
                                    description='Message shown when audit log data is copied to clipboard'
                                    defaultMessage='Audit log data copied to clipboard'
                                />
                            );
                        }}
                    />
                </div>
                <div className='no-scrollbar flex max-h-[500px] w-full flex-col gap-2 overflow-auto'>
                    {Object.entries(props.audit.data).map(([key, value]) => (
                        <CollapsibleAuditData
                            key={key}
                            dataKey={key}
                            value={value}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

const CollapsibleAuditData = (props: { dataKey: string; value: any }) => {
    const [open, setOpen] = useState(false);

    const isObject = typeof props.value === 'object' && props.value !== null && !Array.isArray(props.value);

    return (
        <div className='flex flex-col gap-1'>
            <div className='text-foreground-secondary flex items-center gap-2 text-sm'>
                {props.dataKey}
                {isObject && (
                    <button
                        className='hover:bg-background-default rounded border px-1 py-0.5 text-xs hover:cursor-pointer'
                        onClick={() => setOpen((o) => !o)}
                        aria-label={open ? 'Collapse' : 'Expand'}>
                        {open ? '-' : '+'}
                    </button>
                )}
            </div>
            <div
                onClick={() => {
                    if (!isObject) return;
                    setOpen((o) => !o);
                }}
                className={`bg-background-default border-border-default outline-border-default text-foreground-default flex justify-between rounded p-2 text-sm outline-1 outline-offset-[-1px] ${isObject ? 'hover:cursor-pointer' : ''}`}>
                {!isObject ? (
                    <>
                        {typeof props.value === 'string' || typeof props.value === 'number' ? props.value : JSON.stringify(props.value, null, 2)}
                        <ICONS.BUTTON.COPY
                            className='hover:text-brand-primary size-4 min-w-[16px] hover:cursor-pointer'
                            onClick={async (e) => {
                                e.stopPropagation();
                                await navigator.clipboard.writeText(
                                    typeof props.value === 'string' || typeof props.value === 'number'
                                        ? props.value.toString()
                                        : JSON.stringify(props.value, null, 2)
                                );
                                ToastUtil.success(
                                    <FormattedMessage
                                        id='AuditDataModal.COPY_KEY_SUCCESS'
                                        description='Message shown when audit log data is copied to clipboard'
                                        defaultMessage='{dataKey} copied to clipboard'
                                        values={{ dataKey: props.dataKey }}
                                    />
                                );
                            }}
                        />
                    </>
                ) : (
                    <>
                        <span className='font-mono text-xs'>{open ? '' : '{...}'}</span>
                        <ICONS.BUTTON.COPY
                            className='hover:text-brand-primary size-4 min-w-[16px] hover:cursor-pointer'
                            onClick={async (e) => {
                                e.stopPropagation();
                                await navigator.clipboard.writeText(JSON.stringify(props.value, null, 2));
                                ToastUtil.success(
                                    <FormattedMessage
                                        id='AuditDataModal.COPY_KEY_SUCCESS'
                                        description='Message shown when audit log data is copied to clipboard'
                                        defaultMessage='{dataKey} copied to clipboard'
                                        values={{ dataKey: props.dataKey }}
                                    />
                                );
                            }}
                        />
                    </>
                )}
            </div>
            {isObject && open && (
                <div className='ml-4 flex flex-col gap-1'>
                    {Object.entries(props.value).map(([subKey, subValue]) => (
                        <CollapsibleAuditData
                            key={subKey}
                            dataKey={subKey}
                            value={subValue}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
